import moment from "moment-timezone";

const fallbackDisplay = "--";

const defaultTimeZoneId = "Europe/London";
const defaultCulture = "en-GB";

let configuredTimeZoneId = defaultTimeZoneId;
let configuredCulture = defaultCulture;

/**
 * Call when main config loads (or on logout) so all formatters use the API culture and business time zone.
 */
export function setLocalizationFromMainConfig(config: {
  culture?: string;
  timeZoneId?: string;
} | null): void {
  configuredTimeZoneId = config?.timeZoneId?.trim() || defaultTimeZoneId;
  configuredCulture = config?.culture?.trim() || defaultCulture;
  moment.locale(configuredCulture);
}

function getDisplayTimeZoneId(): string {
  return configuredTimeZoneId;
}

/**
 * Parse a value from the API as a UTC instant (ISO string with Z/offset, or a Date).
 * Does not apply the configured display zone — use formatters for that.
 */
export function parseApiInstant(
  value: string | Date | null | undefined
): moment.Moment | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    const m = moment.utc(value);
    return m.isValid() ? m : null;
  }
  const s = String(value).trim();
  if (!s) {
    return null;
  }
  const strict = moment.utc(s, moment.ISO_8601, true);
  if (strict.isValid()) {
    return strict;
  }
  const loose = moment.utc(s);
  return loose.isValid() ? loose : null;
}

function formatInstantInConfiguredZone(
  instant: moment.Moment | null,
  format: string
): string {
  if (!instant || !instant.isValid()) {
    return fallbackDisplay;
  }
  return instant.clone().tz(getDisplayTimeZoneId()).format(format);
}

export function displayDateOnly(
  value: string | Date | null | undefined
): string {
  const m = parseApiInstant(value);
  return formatInstantInConfiguredZone(m, "D MMM YYYY");
}

export function displayDateTime(
  value: string | Date | null | undefined
): string {
  const m = parseApiInstant(value);
  return formatInstantInConfiguredZone(m, "D MMM YYYY HH:mm");
}

/** Recall list: omit year when same as current year in the configured business zone. */
export function formatRecallRequestedAtForList(
  value: string | Date | null | undefined
): string | null | undefined {
  if (value === null || value === undefined) {
    return value as null | undefined;
  }
  const m = parseApiInstant(value);
  if (!m) {
    return typeof value === "string" ? value : fallbackDisplay;
  }
  const zoned = m.clone().tz(getDisplayTimeZoneId());
  const currentYear = moment.tz(getDisplayTimeZoneId()).year();
  if (zoned.year() === currentYear) {
    return zoned.format("DD MMM HH:mm");
  }
  return zoned.format("DD MMM YYYY HH:mm");
}

export function isValidIsoDateString(value: unknown): boolean {
  if (!value || typeof value !== "string") {
    return false;
  }
  const s = value.trim();
  if (!s) {
    return false;
  }
  // Recall refs and other codes can be numeric (e.g. "1065"). Moment's ISO_8601 strict mode
  // can still treat a bare 4-digit year as a date — require calendar/time structure.
  if (!s.includes("-") && !s.includes("T")) {
    return false;
  }
  const match = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3})/);
  const trimmed = match ? match[1] : s;
  const ISO_FULL = "YYYY-MM-DD[T]HH:mm:ss.SSS";
  const parsed = moment.utc(trimmed, ISO_FULL, true);
  if (parsed.isValid()) {
    return true;
  }
  return moment.utc(s, moment.ISO_8601, true).isValid();
}

export const getCurrentFormattedDate = (): string => {
  return formatInstantInConfiguredZone(moment.utc(), "D MMM YYYY HH:mm");
};

export const getCurrentFormattedTime = (): string => {
  return formatInstantInConfiguredZone(moment.utc(), "D MMM YYYY HH:mm");
};

export const getCurrentFormattedOnlyDate = (): string => {
  return formatInstantInConfiguredZone(moment.utc(), "D MMM YYYY");
};

export const getFormattedTime = (date: Date): string => {
  const m = parseApiInstant(date);
  if (!m) {
    return fallbackDisplay;
  }
  return m
    .clone()
    .tz(getDisplayTimeZoneId())
    .locale(configuredCulture)
    .format("LT");
};

moment.locale(defaultCulture);
